import { useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { recordHit } from 'js/utils/record_hit';

/**
 * Record `react-router-dom` transitions as `Analytics::Hit`
 * Must be called from a subcomponent of Router
 * @param basename `basename` as supplied to BrowserRouter
 */
export default function useRouterHits(basename?: string) {
  const [referrer, setReferrer] = useState<string | null>(null);
  const baseUrl = `https://www.${$app_host}`;

  const location = useLocation();

  useEffect(() => {
    if (!location) {
      // If we're not in a Router, silently fail.
      return;
    }

    // Rebuild the complete URL from the location
    const url = new URL(`${basename || ''}${location.pathname}`, baseUrl);
    url.hash = location.hash;
    url.search = location.search;

    const newUrl = url.toString();

    // Do not record a client-side hit for the initial page load
    // Backend will already record this, so we don't want duplicate hits
    if (!referrer) {
      setReferrer(newUrl);
      return;
    }

    // Sometimes, the location from `useLocation()` changes even if the url
    // hasn't changed. Don't send duplicate hits in those cases.
    if (referrer === newUrl) {
      return;
    }

    recordHit(
      {
        url: newUrl,
        referrer: referrer,
        // Request ID will be set automatically
      },
      true,
    ).then((response) => {
      if (response) {
        // Update the global hit data so new requests will reference the updated
        // hit.
        // NOTE: this is not perfect, because some requests might be initiated
        // and use the previous hit data before this response has gone through
        // and this has had a chance to update. This will cause some requests to
        // be attributed to the previous hit. A larger effort to fix this would
        // be more complicated (perhaps storing a promise here and having things
        // wait for the promise to resolve before sending).
        window.Aedu.hit_data = response;
      }
    });

    setReferrer(newUrl);
  }, [location, baseUrl, basename, referrer]);
}
